import { useState } from 'react'
import cx from 'classnames'
import { Button } from 'blaze-ui/Button'
import { Check02 } from 'blaze-ui/icons/16'
import { Stars, Layers3, Layers1 } from 'blaze-ui/icons/20'
import { SourceImageEditLevel } from 'types'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { TopBar } from '../TopBar/TopBar'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { reportError } from 'utils/error'
import Styles from './V4ContentPreferences.module.scss'

export interface V4ContentPreferencesProps {
  onNext: (sourceImageEditLevel: SourceImageEditLevel) => void
  onBack: () => void
  brandKitId: number
  initialVariant: SourceImageEditLevel
}

const ICON_CLASS_MAP: Record<SourceImageEditLevel, string> = {
  high: Styles.optionIconHigh,
  medium: Styles.optionIconMedium,
  low: Styles.optionIconLow,
  none: Styles.optionIconLow,
}

const ICON_COLOR_MAP: Record<SourceImageEditLevel, string> = {
  high: 'var(--upgrade)',
  medium: 'var(--growth)',
  low: 'var(--green)',
  none: 'var(--green)',
}

const OPTIONS: {
  variant: SourceImageEditLevel
  label: string
  description: string
  icon: React.ElementType
}[] = [
  {
    variant: 'high',
    label: 'Full Freedom',
    description: 'Blaze can significantly transform your photos for the best possible result.',
    icon: Stars,
  },
  {
    variant: 'medium',
    label: 'Balanced',
    description: 'Blaze makes thoughtful edits but keeps your photos recognizable.',
    icon: Layers3,
  },
  {
    variant: 'low',
    label: 'Minimal changes',
    description: 'Blaze only adjusts lighting and minor details. Your originals stay mostly as-is.',
    icon: Layers1,
  },
]

const CDN = 'https://res.cloudinary.com/almanac/image/upload'

const EXAMPLE_IMAGES: Record<SourceImageEditLevel, string> = {
  high: `${CDN}/blaze_assets/virtual_marketer/onboarding/bb1_th3hrm.png`,
  medium: `${CDN}/blaze_assets/virtual_marketer/onboarding/bb2_b8iktj.png`,
  low: `${CDN}/blaze_assets/virtual_marketer/onboarding/bb3_men5xe.png`,
  none: `${CDN}/blaze_assets/virtual_marketer/onboarding/bb4_idksf4.png`,
}

export const V4ContentPreferences = ({ onNext, onBack, brandKitId, initialVariant }: V4ContentPreferencesProps) => {
  const [variant, setVariant] = useState<SourceImageEditLevel>(initialVariant || 'high')
  const [isSaving, setIsSaving] = useState(false)

  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const { mutateAsync: updateBrandKit, isLoading: isUpdatingBrandKit } = useUpdateBrandKit({
    id: brandKitId,
  })
  const { isMobile } = useResponsiveDesign()

  const handleNext = async () => {
    setIsSaving(true)
    try {
      await updateBrandKit({
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          source_image_edit_level: variant,
        },
      })
      onNext(variant)
    } catch (err) {
      reportError(err)
    } finally {
      setIsSaving(false)
    }
  }

  if (isMobile) {
    return (
      <div className={Styles.mobileRoot}>
        <div className={Styles.mobileContent}>
          <img
            src={EXAMPLE_IMAGES[variant] ?? EXAMPLE_IMAGES.high}
            alt={variant}
            className={Styles.mobileHeroImage}
            draggable={false}
          />
          <div className={Styles.mobileContentInner}>
            <h1 className={Styles.mobileHeading}>Choose how much Blaze should enhance your content.</h1>
            <div className={Styles.mobileOptionList}>
              {OPTIONS.map(({ variant: v, label, description }) => (
                <button
                  key={v}
                  type="button"
                  className={cx(Styles.mobileOptionCard, {
                    [Styles.mobileOptionCardSelected]: variant === v,
                  })}
                  onClick={() => setVariant(v)}
                  aria-pressed={variant === v}
                >
                  <span className={Styles.mobileOptionLabel}>{label}</span>
                  <span className={Styles.mobileOptionDescription}>{description}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className={Styles.mobileFooter}>
          <Button size="lg" onPress={handleNext} isDisabled={isSaving || isUpdatingBrandKit} fullWidth>
            {isSaving ? 'Saving...' : 'Start Generating Content'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <MultiStepStepLayout headingLevel={1} scrollContainerClassName={Styles.root} contentClassName={Styles.contentReset}>
      <div className={Styles.page}>
        <TopBar highlightStep="content" />
        <div className={Styles.columns}>
          <div className={Styles.leftCol}>
            <div className={Styles.scrollArea}>
              <div className={Styles.panelHeader}>
                <h1 className={Styles.heading}>When Blaze uses your photos, how far can it go?</h1>
                <p className={Styles.subtitle}>
                  Blaze generates a new image matching the topic of the post. Choose how much it can change from the
                  original.
                </p>
              </div>

              <div className={Styles.optionList}>
                {OPTIONS.map(({ variant: v, label, description, icon: Icon }) => (
                  <button
                    key={v}
                    type="button"
                    className={cx(Styles.optionCard, {
                      [Styles.optionCardSelected]: variant === v,
                    })}
                    onClick={() => setVariant(v)}
                    aria-pressed={variant === v}
                  >
                    <div className={cx(Styles.optionIcon, ICON_CLASS_MAP[v])}>
                      <Icon size={16} color={ICON_COLOR_MAP[v]} />
                    </div>
                    <div className={Styles.optionText}>
                      <span className={Styles.optionLabel}>{label}</span>
                      <span className={Styles.optionDescription}>{description}</span>
                    </div>
                    <div
                      className={cx(Styles.checkIndicator, {
                        [Styles.checkIndicatorSelected]: variant === v,
                      })}
                      aria-hidden
                    >
                      {variant === v && <Check02 size={15} color="white" />}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className={Styles.footer}>
              <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isSaving}>
                Back
              </Button>
              <Button size="lg" onPress={handleNext} isDisabled={isSaving || isUpdatingBrandKit}>
                {isSaving ? 'Saving...' : 'Continue'}
              </Button>
            </div>
          </div>

          <div className={Styles.rightCol}>
            <div className={Styles.imageWrap}>
              <img
                src={EXAMPLE_IMAGES[variant] ?? EXAMPLE_IMAGES.high}
                alt={variant}
                className={Styles.image}
                draggable={false}
              />
            </div>
          </div>
        </div>
      </div>
    </MultiStepStepLayout>
  )
}
